<template>
    <component :is="wrapper_component" :title="title">
        <v-container :class="container_class">
            <link-back
                v-if="show_back_link && back_link_text && back_link_to"
                class="mb-4"
                :text="back_link_text"
                :to="back_link_to"
            />

            <slot />
        </v-container>
    </component>
</template>

<script>
/**
 * SmartPageLayout
 * 
 * A layout component that can be used either as a router-level page (with m-pageframe)
 * or within a dialog (without page frame). This provides a consistent container layout
 * that adapts to its usage context.
 * 
 * Usage as a router page:
 * <smart-page-layout
 *     title="My Page Title"
 *     back-link-text="Back to List"
 *     :back-link-to="{ name: 'list-page' }"
 * >
 *     <p>Page content here</p>
 * </smart-page-layout>
 * 
 * Usage in a dialog:
 * <smart-page-layout
 *     :is-router-page="false"
 *     title="Dialog Content"
 * >
 *     <p>Dialog content here</p>
 * </smart-page-layout>
 */
export default {
    name: 'SmartPageLayout',

    props: {
        /**
         * Whether this component is being used as a router-level page.
         * When true, wraps content in m-pageframe (full page layout).
         * When false, wraps content in a simple div (for use in dialogs).
         */
        is_router_page: {
            type: Boolean,
            default: true,
        },

        /**
         * The page title shown in the page frame header.
         * Only applies when is_router_page is true.
         */
        title: {
            type: String,
            default: '',
        },

        /**
         * Text for the back link (e.g., "Back to List").
         * Only shown when is_router_page is true and both this and back_link_to are provided.
         */
        back_link_text: {
            type: String,
            default: null,
        },

        /**
         * Router location object for the back link (e.g., { name: 'list-page' }).
         * Only shown when is_router_page is true and both this and back_link_text are provided.
         */
        back_link_to: {
            type: [Object, String],
            default: null,
        },

        /**
         * Additional CSS classes to apply to the v-container.
         */
        container_class: {
            type: String,
            default: '',
        },
    },

    computed: {
        /**
         * Determines which wrapper component to use based on is_router_page prop.
         * Returns 'm-pageframe' for router pages, 'div' for dialog usage.
         */
        wrapper_component() {
            return this.is_router_page ? 'm-pageframe' : 'div';
        },

        /**
         * Whether to show the back link.
         * Only shown for router pages when both text and target are provided.
         */
        show_back_link() {
            return this.is_router_page;
        },
    },
};
</script>
